/* eslint-disable @typescript-eslint/no-explicit-any */
import { BoxProps, FormField, FormFieldProps, Keyboard, TextInput, TextInputProps, TextProps } from 'grommet';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/** Formats a number as a USD currency string. decimals=true forces 2 decimals; decimals=false omits them. */
const formatCurrency = (n: number, decimals = true): string => {
  if (isNaN(n)) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals ? 2 : 0,
    maximumFractionDigits: decimals ? 2 : 0,
  }).format(n);
};

/** Parses a (possibly formatted) string into a number, stripping anything that isn't a digit or dot. */
const parseCurrency = (s: string): number => {
  return parseFloat(s.replace(/[^0-9.]/g, ''));
};

/** Strips non-numeric chars, keeping only the first dot so "1.2.3" -> "1.23". */
const sanitizeNumeric = (s: string): string => {
  const cleaned = s.replace(/[^0-9.]/g, '');
  const dot = cleaned.indexOf('.');
  if (dot === -1) return cleaned;
  return cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/\./g, '');
};

/** Counts significant chars ([0-9.]) in s — commas/$ are ignored for caret mapping. */
const countSig = (s: string): number => (s.match(/[0-9.]/g) || []).length;

/** Index in `s` after the Nth significant char (used to restore caret across comma/$ insertions/removals). */
const indexOfSig = (s: string, n: number): number => {
  let count = 0;
  for (let i = 0; i < s.length; i++) {
    if (/[0-9.]/.test(s[i])) {
      count++;
      if (count === n) return i + 1;
    }
  }
  return s.length;
};

export interface ITalonInputProps extends TextInputProps {
  id?: string;
  name?: string;
  label?: string;
  htmlFor?: string;
  val: string | number;
  onChange?: (val: any) => void;
  onBlur?: (val?: any) => void;
  onSubmit?: (val?: any) => void;
  minValue?: number;
  maxValue?: number;
  minLength?: number;
  maxLength?: number;
  width?: string;
  margin?: BoxProps['margin'];
  textSize?: TextProps['size'];
  inputType?: 'text' | 'number' | 'email' | 'password' | 'search' | 'tel' | 'url' | 'time';
  /** Debounce onChange calls by this many milliseconds. Useful to avoid API calls on every keystroke. */
  saveDelay?: number;
  /** When 'currency', val is a number displayed as a formatted USD string, and onChange emits a number. */
  format?: 'currency';
  /** In currency mode: true (default) forces 2 decimals ($1,234.00, emits floats); false shows whole dollars ($1,234, emits integers). */
  currencyDecimals?: boolean;
  /** Form field props */
  formFieldBackground?: BoxProps['background'];
  hideFormFieldElement?: boolean;
  validate?: FormFieldProps['validate'];
  error?: FormFieldProps['error'];
  help?: FormFieldProps['help'];
}

export const TalonInput = ({
  id,
  name,
  label,
  htmlFor,
  // placeholder,
  val,
  onChange,
  onBlur,
  onSubmit,
  minValue,
  maxValue,
  minLength,
  maxLength,
  // required,
  width,
  margin,
  textSize,
  // autoComplete,
  inputType,
  // disabled,
  saveDelay = 500,
  validate,
  format,
  currencyDecimals = true,
  error,
  help,
  formFieldBackground,
  hideFormFieldElement,
  ...textInputProps
}: ITalonInputProps) => {
  const isCurrency = format === 'currency';
  const decimals = currencyDecimals;

  /** State **/
  const [edit, setEdit] = useState<boolean>(false);
  const [value, setValue] = useState<typeof val>(isCurrency ? formatCurrency(Number(val), decimals) : val);
  // Caret position to restore after currency reformatting (currency edit mode only).
  const [selection, setSelection] = useState<[number, number] | null>(null);

  /** Refs **/
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onChangeRef = useRef(onChange);

  /** Methods - Debouncing **/

  // Emits the current input value to onChange, parsing to a number in currency mode.
  const fireChange = useCallback(() => {
    if (!inputRef.current || textInputProps.readOnly) return;
    const raw = inputRef.current.value;
    onChangeRef.current?.(isCurrency ? parseCurrency(raw) || 0 : raw);
  }, [textInputProps.readOnly, isCurrency]);

  // Fire any pending debounced onChanges immediately
  const flush = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    fireChange();
  }, [fireChange]);

  // Schedules a debounced onChange
  const scheduleChange = useCallback(() => {
    if (saveDelay === undefined || saveDelay === 0) {
      // No debounce requested — fire immediately.
      fireChange();
      return;
    }
    if (timerRef.current !== null) {
      // Reset the timer each keystroke
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      fireChange();
    }, saveDelay);
  }, [saveDelay, fireChange]);

  /** Methods - Event Handlers **/

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isCurrency) {
      if (decimals) {
        // ATM-style fixed-decimal entry: digits shift in from the right of the fixed decimal.
        const prevDisplay = String(value);
        const oldCents = Math.round((parseCurrency(prevDisplay) || 0) * 100);
        const oldDigits = prevDisplay.replace(/[^0-9]/g, '');
        const newStr = e.target.value;
        const newDigits = newStr.replace(/[^0-9]/g, '');

        let cents: number;
        if (newDigits.length === oldDigits.length + 1 && newDigits.startsWith(oldDigits)) {
          // A digit was appended at the end -> shift left by one decimal place.
          const d = parseInt(newDigits.slice(-1), 10);
          cents = oldCents * 10 + d;
        } else if (oldDigits.length === newDigits.length + 1 && oldDigits.startsWith(newDigits)) {
          // A digit was removed (backspace) -> shift right by one decimal place.
          cents = Math.floor(oldCents / 10);
        } else {
          // Paste / select-replace / clear -> reparse the whole string, defaulting to 0.
          cents = Math.round((parseCurrency(newStr) || 0) * 100);
        }

        const formatted = formatCurrency(cents / 100, true);
        setValue(formatted);
        setSelection([formatted.length, formatted.length]);
        scheduleChange();
        return;
      }

      // Whole-dollar mode: live comma formatting with caret preservation.
      const caret = e.target.selectionStart ?? e.target.value.length;
      const sigBefore = countSig(e.target.value.slice(0, caret));
      const raw = sanitizeNumeric(e.target.value);
      const formatted = formatCurrency(parseCurrency(raw) || 0, false);
      const newCaret = indexOfSig(formatted, sigBefore);
      setValue(formatted);
      setSelection([newCaret, newCaret]);
      scheduleChange();
      return;
    }

    if (e.target.value) {
      const v = typeof e.target.value === 'number' ? (e.target.value as number).toString() : e.target.value;
      if (minLength && v.length < minLength) return;
      if (maxLength && v.length > maxLength) return;
    }
    setValue(e.target.value || '');
    scheduleChange();
  };

  const handleSubmit = () => {
    flush();
    setEdit(false);
    onSubmit && onSubmit();
  };

  const handleBlur = () => {
    flush();
    setEdit(false);
    onBlur && onBlur();
  };

  const takeFocus = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (isCurrency) {
      const formatted = !isNaN(Number(val)) ? formatCurrency(Number(val), decimals) : '';
      setValue(formatted);
      // In float (ATM) mode, pin the caret to the end so the first keystroke appends cleanly.
      if (decimals) {
        setSelection([formatted.length, formatted.length]);
      }
    }
    setEdit(true);
  };

  /** Effects **/

  // Restore caret after currency reformatting renders the new value.
  useLayoutEffect(() => {
    if (selection && inputRef.current) {
      inputRef.current.selectionStart = selection[0];
      inputRef.current.selectionEnd = selection[1];
    }
  }, [selection, value]);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (edit) return;
    if (isCurrency) {
      setValue(formatCurrency(Number(val), decimals));
    } else {
      setValue(val);
    }
  }, [val, edit, isCurrency, decimals]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  /** Render **/

  const renderTextInput = () => (
    <Keyboard onEnter={handleSubmit}>
      <TextInput
        ref={inputRef}
        id={id || htmlFor || name}
        name={name || htmlFor || id}
        value={value || ''}
        autoFocus={edit}
        onClick={takeFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        type={isCurrency ? 'text' : inputType}
        min={minValue}
        max={maxValue}
        minLength={minLength}
        maxLength={maxLength}
        plain={inputType !== 'number'}
        size={textSize}
        width={width}
        {...textInputProps}
      />
    </Keyboard>
  );

  const showFormFieldElement =
    !hideFormFieldElement && (label || htmlFor || margin || validate || error || help || formFieldBackground);

  return showFormFieldElement ? (
    <FormField
      label={label}
      htmlFor={htmlFor || name || id}
      style={{ width }}
      margin={margin}
      validate={validate}
      error={error}
      help={help}
      background={formFieldBackground}
    >
      {renderTextInput()}
    </FormField>
  ) : (
    renderTextInput()
  );
};
